import React from 'react';
import { Box, VStack, HStack, Text, Heading, Textarea, Button, Avatar, IconButton, useColorModeValue, Spinner, Link as ChakraLink, Badge, Tooltip } from '@chakra-ui/react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listComments, createComment, updateComment, deleteComment, CommentItem, reactComment, unreactComment, requestUnban, reportComment } from '../../services/comments';
import { useAuth } from '../../contexts/AuthContext';
import { Pencil, Trash2, Send, CheckCircle2 } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

type Props = {
  targetType: 'article' | 'event' | 'gallery_album' | 'youtube_video';
  targetId: string;
};

const PAGE_SIZE = 20;

const displayName = (u?: CommentItem['user']) => {
  const { i18n } = useTranslation();
  const useEnglish = i18n.language === 'en';
  
  if (!u) return useEnglish ? 'Anonymous' : 'Anonym';
  const uname = (u.username || '').trim();
  if (uname) return uname;
  const name = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return name || (useEnglish ? 'User' : 'Uživatel');
};

const CommentsSection: React.FC<Props> = ({ targetType, targetId }) => {
  const { t, i18n } = useTranslation();
  const cardBg = useColorModeValue('white', 'gray.800');
  const border = useColorModeValue('gray.200', 'gray.700');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const appealBg = useColorModeValue('gray.50','gray.700');
  const adminLikedColor = useColorModeValue('blue.600','blue.300');
  const queryClient = useQueryClient();
  const { isAuthenticated, user } = useAuth();
  
  const useEnglish = i18n.language === 'en';

  const commentsQuery = useInfiniteQuery({
    queryKey: ['comments', targetType, targetId],
    queryFn: ({ pageParam = 1 }) => listComments({ target_type: targetType, target_id: targetId, page: pageParam, page_size: PAGE_SIZE }),
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, p) => sum + (p.items?.length || 0), 0);
      return loaded < lastPage.total ? pages.length + 1 : undefined;
    },
    staleTime: 30_000,
  });

  const allItems = (commentsQuery.data?.pages || []).flatMap(p => p.items || []);

  const [newContent, setNewContent] = React.useState('');
  const [editingId, setEditingId] = React.useState<number | null>(null);
  const [editContent, setEditContent] = React.useState('');
  const [replyTo, setReplyTo] = React.useState<number | null>(null);
  const [errorMsg, setErrorMsg] = React.useState<string | null>(null);
  const [canRequestUnban, setCanRequestUnban] = React.useState<boolean>(false);
  const [unbanMessage, setUnbanMessage] = React.useState<string>('Please unblock my comments. Thank you.');
  const [localReactions, setLocalReactions] = React.useState<Record<number, string | undefined>>({});
  const [pendingReactions, setPendingReactions] = React.useState<Set<number>>(new Set());

  const createMut = useMutation({
    mutationFn: (body: { content: string; parent_id?: number | null }) => createComment({ target_type: targetType, target_id: targetId, content: body.content, parent_id: body.parent_id }),
    onSuccess: async (created) => {
      setNewContent('');
      setReplyTo(null);
      setErrorMsg(null);
      await queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] });
      if ((created as any)?.status === 'hidden') {
        setErrorMsg(useEnglish ? 'Your comment is awaiting approval (automatic moderation).' : 'Váš komentář čeká na schválení (automatická moderace).');
      }
      try { window.dispatchEvent(new CustomEvent('engagement:refresh')); } catch {}
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.error || (useEnglish ? 'Failed to post comment' : 'Nepodařilo se odeslat komentář');
      setErrorMsg(msg);
      if ((e?.response?.status || 0) === 403) setCanRequestUnban(true);
    }
  });

  const updateMut = useMutation({
    mutationFn: (args: { id: number; content: string }) => updateComment(args.id, { content: args.content }),
    onSuccess: async () => {
      setEditingId(null);
      setEditContent('');
      await queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => deleteComment(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] });
    },
  });

  const reactMut = useMutation({
    mutationFn: (args: { id: number; type: string }) => reactComment(args.id, args.type),
    onMutate: async ({ id, type }) => {
      setPendingReactions(prev => new Set(prev).add(id));
      
      const qk = ['comments', targetType, targetId] as const;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<any>(qk);
      
      // Set optimistic state
      setLocalReactions((m) => ({ ...m, [id]: type }));
      
      queryClient.setQueryData(qk, (oldData: any) => {
        if (!oldData) return oldData;
        const pages = (oldData.pages || []).map((page: any) => {
          const items = (page.items || []).map((it: any) => {
            if (it.id !== id) return it;
            const next = { ...it, reactions: { ...(it.reactions || {}) } };
            const prevType = (next.my_reaction as string | undefined)?.trim();
            
            // Remove previous reaction count if it existed
            if (prevType && prevType !== type) {
              const prevCount = (typeof next.reactions[prevType] === 'number' ? next.reactions[prevType] : 1) as number;
              const newPrev = Math.max(0, prevCount - 1);
              if (newPrev <= 0) {
                delete (next.reactions as any)[prevType];
              } else {
                next.reactions[prevType] = newPrev;
              }
            }
            
            // Add new reaction count
            next.reactions[type] = ((next.reactions[type] || 0) as number) + 1;
            next.my_reaction = type;
            
            if ((user as any)?.role === 'admin') {
              next.admin_liked = (type === 'thumbs_up' || type === 'like');
            }
            return next;
          });
          return { ...page, items };
        });
        return { ...oldData, pages };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      const qk = ['comments', targetType, targetId] as const;
      if ((ctx as any)?.previous) {
        queryClient.setQueryData(qk, (ctx as any).previous);
      }
    },
    onSettled: async (_data, _error, variables) => {
      if (variables?.id) {
        setPendingReactions(prev => {
          const next = new Set(prev);
          next.delete(variables.id);
          return next;
        });
      }
      // Clear local optimistic state after a delay to allow server state to sync
      setTimeout(() => {
        setLocalReactions((m) => {
          const cp = { ...m };
          delete cp[variables?.id];
          return cp;
        });
      }, 100);
      
      await queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] });
      try { window.dispatchEvent(new CustomEvent('engagement:refresh')); } catch {}
    },
  });

  const unreactMut = useMutation({
    mutationFn: (id: number) => unreactComment(id),
    onMutate: async (id: number) => {
      setPendingReactions(prev => new Set(prev).add(id));
      
      const qk = ['comments', targetType, targetId] as const;
      await queryClient.cancelQueries({ queryKey: qk });
      const previous = queryClient.getQueryData<any>(qk);
      
      // Set optimistic state to empty
      setLocalReactions((m) => ({ ...m, [id]: '' }));
      
      queryClient.setQueryData(qk, (oldData: any) => {
        if (!oldData) return oldData;
        const pages = (oldData.pages || []).map((page: any) => {
          const items = (page.items || []).map((it: any) => {
            if (it.id !== id) return it;
            const next = { ...it, reactions: { ...(it.reactions || {}) } };
            const prevType = (next.my_reaction as string | undefined)?.trim();
            if (prevType) {
              const prevCount = (typeof next.reactions[prevType] === 'number' ? next.reactions[prevType] : 1) as number;
              const newPrev = Math.max(0, prevCount - 1);
              if (newPrev <= 0) {
                delete (next.reactions as any)[prevType];
              } else {
                next.reactions[prevType] = newPrev;
              }
            }
            next.my_reaction = '';
            if ((user as any)?.role === 'admin') {
              next.admin_liked = false;
            }
            return next;
          });
          return { ...page, items };
        });
        return { ...oldData, pages };
      });
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      const qk = ['comments', targetType, targetId] as const;
      if ((ctx as any)?.previous) {
        queryClient.setQueryData(qk, (ctx as any).previous);
      }
    },
    onSettled: async (_data, _error, variables) => {
      if (variables) {
        setPendingReactions(prev => {
          const next = new Set(prev);
          next.delete(variables);
          return next;
        });
      }
      // Clear local optimistic state after a delay to allow server state to sync
      setTimeout(() => {
        setLocalReactions((m) => {
          const cp = { ...m };
          delete cp[variables];
          return cp;
        });
      }, 100);
      
      await queryClient.invalidateQueries({ queryKey: ['comments', targetType, targetId] });
      try { window.dispatchEvent(new CustomEvent('engagement:refresh')); } catch {}
    },
  });

  const unbanMut = useMutation({
    mutationFn: (message: string) => requestUnban(message),
    onSuccess: () => {
      setCanRequestUnban(false);
      setErrorMsg(useEnglish ? 'Unban request sent.' : 'Žádost o odblokování odeslána.');
      setUnbanMessage(useEnglish ? 'Please unblock my comments. Thank you.' : 'Prosím o odblokování komentářů. Děkuji.');
    }
  });

  const reportMut = useMutation({
    mutationFn: (args: { id: number; reason?: string }) => reportComment(args.id, args.reason),
    onSuccess: async () => {
      setErrorMsg(useEnglish ? 'Thank you for reporting. A moderator will review the comment.' : 'Děkujeme za nahlášení. Moderátor se na komentář podívá.');
    },
  });

  const canEdit = (c: CommentItem) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    return Number(user.id) === Number(c.user?.id);
  };

  const minChars = 6;

  // Build simple threaded structure
  const byParent: Record<string, CommentItem[]> = React.useMemo(() => {
    const map: Record<string, CommentItem[]> = {};
    for (const c of allItems) {
      const key = String(c.parent_id || 0);
      (map[key] = map[key] || []).push(c);
    }
    return map;
  }, [allItems]);

  const ReactionBar: React.FC<{ c: CommentItem }> = ({ c }) => {
    const options: { key: string; label: string; color: string; name: string }[] = [
      { key: 'thumbs_up', label: '👍', color: 'green', name: useEnglish ? 'Thumbs up' : 'Palec nahoru' },
      { key: 'heart', label: '❤️', color: 'pink', name: useEnglish ? 'Heart' : 'Srdíčko' },
      { key: 'smile', label: '😀', color: 'yellow', name: useEnglish ? 'Smile' : 'Úsměv' },
      { key: 'surprised', label: '😮', color: 'purple', name: useEnglish ? 'Surprised' : 'Překvapení' },
      { key: 'thumbs_down', label: '👎', color: 'red', name: useEnglish ? 'Thumbs down' : 'Palec dolů' },
    ];
    const counts = c.reactions || {};
    const active = React.useMemo(() => {
      // Prioritize local optimistic state, fallback to server state
      const localActive = (localReactions[c.id] || '').trim();
      if (localActive !== '') {
        return localActive;
      }
      return (c.my_reaction || '').trim();
    }, [c.my_reaction, localReactions, c.id]);
    
    const viewCounts = React.useMemo(() => {
      const base: Record<string, number> = { ...(counts as any) };
      const a = (active || '').trim();
      const serverActive = (c.my_reaction || '').trim();
      const localActive = (localReactions[c.id] || '').trim();
      
      // If we have a local optimistic state that differs from server, adjust counts
      if (localActive !== '' && localActive !== serverActive) {
        // Remove old server reaction count if it existed
        if (serverActive) {
          const prev = typeof base[serverActive] === 'number' ? base[serverActive] : 0;
          if (prev > 0) {
            base[serverActive] = prev - 1;
            if (base[serverActive] <= 0) delete base[serverActive];
          }
        }
        // Add new local reaction count
        base[localActive] = (base[localActive] || 0) + 1;
      }
      // Handle unreact optimistic state (empty string)
      else if (localActive === '' && serverActive) {
        // Remove the server reaction count optimistically
        const prev = typeof base[serverActive] === 'number' ? base[serverActive] : 0;
        if (prev > 0) {
          base[serverActive] = prev - 1;
          if (base[serverActive] <= 0) delete base[serverActive];
        }
      }
      
      return base;
    }, [counts, active, c.my_reaction, localReactions, c.id]);
    const isBusy = reactMut.isPending || unreactMut.isPending || pendingReactions.has(c.id);
    return (
      <HStack spacing={2} mt={1}>
        {options.map((o) => (
          <Tooltip key={o.key} label={o.name} placement="top" hasArrow>
            <Button
              size="xs"
              colorScheme={o.color}
              variant={active === o.key ? 'solid' : 'outline'}
              isDisabled={!isAuthenticated || isBusy}
              isActive={active === o.key}
              onClick={() => {
                if (!isAuthenticated || isBusy) return;
                if (active === o.key) {
                  unreactMut.mutate(c.id);
                } else {
                  reactMut.mutate({ id: c.id, type: o.key });
                }
              }}
            >
              <HStack spacing={1}>
                <Text as="span">{o.label}</Text>
                <Text as="span" fontSize="xs">{viewCounts[o.key] || 0}</Text>
              </HStack>
            </Button>
          </Tooltip>
        ))}
      </HStack>
    );
  };

  const renderThread = (parentId: number | null, depth = 0) => {
    const list = byParent[String(parentId || 0)] || [];
    return (
      <VStack align="stretch" spacing={3} pl={depth ? 6 : 0}>
        {list.map((c) => (
          <HStack key={c.id} align="start" spacing={3} borderWidth="1px" borderColor={border} borderRadius="md" p={3}>
            <Avatar name={displayName(c.user)} size="sm" src={(c as any)?.user?.avatar_url || undefined} />
            <VStack align="stretch" spacing={1} flex={1}>
              <HStack justify="space-between">
                <HStack spacing={2}>
                  <Text fontWeight="600">{displayName(c.user)}</Text>
                  {c.user?.role === 'admin' && <Badge colorScheme="purple" variant="subtle">{useEnglish ? 'Admin' : 'Admin'}</Badge>}
                  <Text fontSize="sm" color={muted}>{new Date(c.created_at).toLocaleString()}</Text>
                  {c.is_edited && <Text fontSize="xs" color={muted}>({useEnglish ? '(edited)' : '(upraveno)'})</Text>}
                </HStack>
                {canEdit(c) && (
                  <HStack spacing={1}>
                    <IconButton aria-label={useEnglish ? 'Edit' : 'Upravit'} size="xs" variant="ghost" icon={<Pencil size={16} />} onClick={() => { setEditingId(c.id); setEditContent(c.content); }} />
                    <IconButton aria-label={useEnglish ? 'Delete' : 'Smazat'} size="xs" variant="ghost" colorScheme="red" icon={<Trash2 size={16} />} onClick={() => deleteMut.mutate(c.id)} />
                  </HStack>
                )}
              </HStack>
              {editingId === c.id ? (
                <VStack align="stretch" spacing={2}>
                  <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={3} />
                  <HStack>
                    <Button size="sm" colorScheme="blue" onClick={() => updateMut.mutate({ id: c.id, content: editContent.trim() })} isLoading={updateMut.isPending}>{useEnglish ? 'Save' : 'Uložit'}</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setEditingId(null); setEditContent(''); }}>{useEnglish ? 'Cancel' : 'Zrušit'}</Button>
                  </HStack>
                </VStack>
              ) : (
                c.content_html ? (
                  <Box sx={{ '.cw': { textDecoration: 'underline dotted', cursor: 'help' } }} dangerouslySetInnerHTML={{ __html: c.content_html }} />
                ) : (
                  <Text whiteSpace="pre-wrap">{c.content}</Text>
                )
              )}
              <ReactionBar c={c} />
              {c.admin_liked && (
                <HStack spacing={2} mt={1} color={adminLikedColor}>
                  <CheckCircle2 size={16} />
                  <Text fontSize="sm">{useEnglish ? 'Marked by administrator' : 'Označeno administrátorem'}</Text>
                </HStack>
              )}
              <HStack>
                {isAuthenticated && <Button size="xs" variant="ghost" onClick={() => setReplyTo(c.id)}>{useEnglish ? 'Reply' : 'Odpovědět'}</Button>}
                {isAuthenticated && <Button size="xs" variant="ghost" colorScheme="red" onClick={() => reportMut.mutate({ id: c.id })}>{useEnglish ? 'Report' : 'Nahlásit'}</Button>}
                {c.status === 'hidden' && <Badge colorScheme="yellow">{useEnglish ? 'Awaiting approval' : 'Čeká na schválení'}</Badge>}
              </HStack>
              {/* Replies */}
              {renderThread(c.id, depth + 1)}
            </VStack>
          </HStack>
        ))}
      </VStack>
    );
  };

  return (
    <Box mt={6} borderWidth="1px" borderColor={border} borderRadius="lg" bg={cardBg} p={4}>
      <HStack justify="space-between" align="center" mb={3}>
        <Heading as="h3" size="md">{useEnglish ? 'Comments' : 'Komentáře'}</Heading>
      </HStack>

      {commentsQuery.isLoading ? (
        <HStack><Spinner size="sm" /><Text>{useEnglish ? 'Loading...' : 'Načítám…'}</Text></HStack>
      ) : (
        <VStack align="stretch" spacing={3}>
          {allItems.length === 0 && (
            <Text color={muted}>{useEnglish ? 'No comments yet.' : 'Zatím žádné komentáře.'}</Text>
          )}
          {renderThread(null)}
          {commentsQuery.hasNextPage && (
            <Button onClick={() => commentsQuery.fetchNextPage()} isLoading={commentsQuery.isFetchingNextPage} alignSelf="center" size="sm" variant="outline">{useEnglish ? 'Load more' : 'Načíst další'}</Button>
          )}
        </VStack>
      )}

      <Box mt={4}>
        {errorMsg && (
          <Text color="orange.500" fontSize="sm" mb={2}>{errorMsg}</Text>
        )}
        {isAuthenticated ? (
          <VStack align="stretch" spacing={2}>
            {replyTo && (
              <HStack justify="space-between">
                <Text fontSize="sm" color={muted}>{useEnglish ? `Reply to comment #${replyTo}` : `Odpověď na komentář #${replyTo}`}</Text>
                <Button size="xs" variant="ghost" onClick={() => setReplyTo(null)}>{useEnglish ? 'Cancel reply' : 'Zrušit odpověď'}</Button>
              </HStack>
            )}
            <Textarea placeholder={useEnglish ? 'Write a comment...' : 'Napište komentář…'} value={newContent} onChange={(e) => setNewContent(e.target.value)} rows={3} />
            <HStack>
              <Button leftIcon={<Send size={16} />} colorScheme="blue" onClick={() => createMut.mutate({ content: newContent.trim(), parent_id: replyTo })} isLoading={createMut.isPending} isDisabled={newContent.trim().length < minChars}>{useEnglish ? 'Send' : 'Odeslat'}</Button>
              <Text fontSize="sm" color={muted}>{useEnglish ? 'Please respect the rules of decent discussion.' : 'Respektujte prosím pravidla slušné diskuse.'}</Text>
            </HStack>
            {canRequestUnban && (
              <VStack align="stretch" spacing={2} borderWidth="1px" borderColor={border} borderRadius="md" p={3} bg={appealBg}>
                <Text fontSize="sm" color={muted}>{useEnglish ? 'Your account is temporarily blocked from commenting. You can send an unban request with a brief explanation.' : 'Váš účet je dočasně zablokován pro komentování. Můžete odeslat žádost o odblokování s krátkým vysvětlením.'}</Text>
                <Textarea
                  placeholder={useEnglish ? 'Your message for administrators...' : 'Vaše zpráva pro administrátory…'}
                  value={unbanMessage}
                  onChange={(e) => setUnbanMessage(e.target.value)}
                  rows={3}
                />
                <HStack>
                  <Button size="sm" variant="outline" onClick={() => unbanMut.mutate(unbanMessage.trim() || (useEnglish ? 'Please unblock my comments. Thank you.' : 'Prosím o odblokování komentářů. Děkuji.'))} isLoading={unbanMut.isPending}>{useEnglish ? 'Send unban request' : 'Odeslat žádost o odblokování'}</Button>
                </HStack>
              </VStack>
            )}
          </VStack>
        ) : (
          <Text color={muted}>
            {useEnglish ? 'To add a comment, please ' : 'Pro přidání komentáře se prosím '}
            <ChakraLink as={RouterLink} to="/login" color="blue.500">
              {useEnglish ? 'log in' : 'přihlaste'}
            </ChakraLink>.
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default CommentsSection;
